const stripTrailingSlash = (value: string) => value.replace(/\/+$/, '')

const viteEnv =
  typeof import.meta !== 'undefined' && typeof import.meta.env !== 'undefined'
    ? import.meta.env
    : ({} as Record<string, string | undefined>)

function resolveLocalMainSiteUrl() {
  if (typeof window === 'undefined') {
    return ''
  }

  const { hostname, protocol } = window.location

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:4010`
  }

  return window.location.origin
}

const mainSiteUrl = stripTrailingSlash(viteEnv.VITE_MAIN_SITE_URL ?? resolveLocalMainSiteUrl())
const authApiBaseUrl = stripTrailingSlash(
  viteEnv.VITE_AUTH_API_BASE_URL ?? resolveLocalMainSiteUrl(),
)

function resolveApiBaseUrl() {
  if (authApiBaseUrl) return authApiBaseUrl
  if (mainSiteUrl) return mainSiteUrl
  return ''
}

function resolveEndpoint(explicitValue: string | undefined, fallbackPath: string): string {
  if (explicitValue && explicitValue.trim()) {
    return explicitValue.trim()
  }

  const apiBaseUrl = resolveApiBaseUrl()
  if (apiBaseUrl) {
    return `${apiBaseUrl}${fallbackPath}`
  }

  return fallbackPath
}

const IMAGE_GEN_ENDPOINT = resolveEndpoint(
  viteEnv.VITE_SUBSITE_ALIYUN_IMAGE_GEN_ENDPOINT,
  '/api/subsite/image-gen/aliyun',
)

// ---------------------------------------------------------------------------
// 模型选项（供页面渲染选择器）
// ---------------------------------------------------------------------------

export type ImageModelTier = 'economy' | 'balanced' | 'quality' | 'flagship'

export type AspectRatio = '1:1' | '3:4' | '4:3' | '9:16' | '16:9'

/** 分辨率档位：standard = 标准，hd = 高清 */
export type ResolutionTier = 'standard' | 'hd'

export type ImageModelOption = {
  value: string
  label: string
  description: string
  tier: ImageModelTier
  /** 每张价格（元） */
  price: number
  /** 是否支持负面提示词 */
  supportsNegativePrompt: boolean
  /** 是否异步（生成耗时更长） */
  isAsync: boolean
}

export const IMAGE_MODEL_OPTIONS: readonly ImageModelOption[] = [
  // 经济档
  {
    value: 'wanx2.0-t2i-turbo',
    label: '万相 2.0 Turbo',
    description: '最经济，适合快速出图和批量生成。',
    tier: 'economy',
    price: 0.04,
    supportsNegativePrompt: true,
    isAsync: true,
  },
  {
    value: 'z-image-turbo',
    label: 'Z-Image Turbo',
    description: '价格低、速度快，中英文文字渲染效果好。',
    tier: 'economy',
    price: 0.1,
    supportsNegativePrompt: false,
    isAsync: false,
  },
  // 均衡档
  {
    value: 'wan2.2-t2i-flash',
    label: '万相 2.2 Flash',
    description: '速度与质量平衡，性价比高。',
    tier: 'balanced',
    price: 0.14,
    supportsNegativePrompt: true,
    isAsync: true,
  },
  {
    value: 'wan2.6-t2i',
    label: '万相 2.6',
    description: '最新万相模型，画质提升，同步生成。',
    tier: 'balanced',
    price: 0.2,
    supportsNegativePrompt: true,
    isAsync: false,
  },
  // 高质量档
  {
    value: 'qwen-image-2.0',
    label: '千问 2.0',
    description: '质量优秀，支持负面提示词，同步生成。',
    tier: 'quality',
    price: 0.2,
    supportsNegativePrompt: true,
    isAsync: false,
  },
  {
    value: 'qwen-image-2.0-pro',
    label: '千问 2.0 Pro',
    description: '千问系列质量最佳，细节表现突出。',
    tier: 'quality',
    price: 0.5,
    supportsNegativePrompt: true,
    isAsync: false,
  },
  {
    value: 'kling/kling-v3-image-generation',
    label: '可灵 v3',
    description: '可灵 AI，画质高，生成耗时较长。',
    tier: 'quality',
    price: 0.2,
    supportsNegativePrompt: false,
    isAsync: true,
  },
  // --- 旗舰档 ---
  {
    value: 'wan2.7-image',
    label: '万相 2.7',
    description: '万相最新旗舰，速度快，支持思考模式增强出图质量。',
    tier: 'flagship',
    price: 0.2,
    supportsNegativePrompt: false,
    isAsync: false,
  },
  {
    value: 'wan2.7-image-pro',
    label: '万相 2.7 Pro',
    description: '万相最强模型，支持 4K 高清输出，细节表现极致。',
    tier: 'flagship',
    price: 0.5,
    supportsNegativePrompt: false,
    isAsync: false,
  },
] as const

export const TIER_LABELS: Record<ImageModelTier, string> = {
  economy: '经济',
  balanced: '均衡',
  quality: '高质量',
  flagship: '旗舰',
}

export const ASPECT_RATIO_OPTIONS: ReadonlyArray<{ value: AspectRatio; label: string }> = [
  { value: '1:1', label: '1:1 正方形' },
  { value: '3:4', label: '3:4 竖版' },
  { value: '4:3', label: '4:3 横版' },
  { value: '9:16', label: '9:16 竖屏' },
  { value: '16:9', label: '16:9 横屏' },
] as const

export const RESOLUTION_OPTIONS: ReadonlyArray<{ value: ResolutionTier; label: string; description: string }> = [
  { value: 'standard', label: '标准', description: '生成更快' },
  { value: 'hd', label: '高清', description: '细节更好' },
] as const

export function getImageModelOption(value: string) {
  return IMAGE_MODEL_OPTIONS.find((option) => option.value === value)
}

function createOutputFileName(model: string) {
  const safeModel = model.toLowerCase().replace(/[^a-z0-9]+/g, '-')
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `ai-image-${safeModel}-${timestamp}.png`
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class AliyunImageService {
  isSupported(): boolean {
    return typeof fetch === 'function'
  }

  async generateImage({
    model,
    prompt,
    negativePrompt,
    aspectRatio,
    resolution,
    onProgress,
  }: {
    model: string
    prompt: string
    negativePrompt?: string
    aspectRatio?: AspectRatio
    resolution?: ResolutionTier
    onProgress?: (stage: string) => void
  }): Promise<{ blob: Blob; file: File; objectUrl: string }> {
    const trimmedPrompt = prompt.trim()
    if (!trimmedPrompt) {
      throw new Error('请输入图片描述。')
    }

    onProgress?.('正在提交生图任务...')

    const body: Record<string, unknown> = {
      model,
      prompt: trimmedPrompt,
    }
    if (negativePrompt && negativePrompt.trim()) {
      body.negativePrompt = negativePrompt.trim()
    }
    if (aspectRatio) {
      body.aspectRatio = aspectRatio
    }
    if (resolution) {
      body.resolution = resolution
    }

    const response = await fetch(IMAGE_GEN_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      let message = 'AI 生图请求失败。'
      try {
        const payload = (await response.json()) as { message?: string; error?: string }
        message = payload.message || payload.error || message
      } catch {
        // ignore invalid JSON body
      }
      throw new Error(message)
    }

    onProgress?.('正在下载生成的图片...')
    const blob = await response.blob()
    if (blob.size === 0) {
      throw new Error('AI 生图没有返回图片数据。')
    }

    // 归一化 MIME 为 image/png（上游可能返回 image/jpeg 等）
    const mimeType = blob.type.startsWith('image/') ? blob.type : 'image/png'
    const file = new File([blob], createOutputFileName(model), {
      type: mimeType,
      lastModified: Date.now(),
    })
    const objectUrl = URL.createObjectURL(blob)

    return { blob, file, objectUrl }
  }
}

export const aliyunImageService = new AliyunImageService()
